import RenderManager from "../rendering/render-manager";
import Utils from "../utils";
import getVWKey from "../helpers/getVWKey";
import AssessmentManager from "../assessment/assessment-manager";
import {simplifyFraction} from "../assessment/assessment-math";
import * as Events from "../utils/events";
import EntranceExit from "./EntranceExit";
import Base2DElementDynamic from "./base-2d-element-dynamic";

const NSString = 'http://www.w3.org/2000/svg'; //DevSkim: ignore DS137138

class MathFormula extends Base2DElementDynamic {

    get type2d() {
        return "MathFormula";
    }

    get getVWSize() {
        return this._getVWSize;
    }

    _initDefaultVars() {
        super._initDefaultVars();
    }

    _initValues(options) {
        this._src = "src" in options ? options.src : this._src;
    }

    get content() {
        return this._content;
    }
    set content(val) {
        this._content = val;
        this.textarea.value = val;
        this.initMathJax()
    }

    constructor(options, parentLayer, uuid = THREE.Math.generateUUID()) {
        super(options, parentLayer, uuid);

        parentLayer.domElement.setAttribute("contains-math", "");

        //dom insertion
        this.domElement = document.createElement('div');
        this.domElement.style.position = "absolute";
        this.parentLayer.domElement.appendChild(this.domElement);
        this.textElement = document.createElement('div');
        this.domElement.className = 'q3-math';
        this.textElement.className = 'mathjax-display';
        this.textElement.style.pointerEvents = "auto";
        this.textElement.style.cursor = this.hasClickActions ? "pointer" : "default";
        this.textElement.style.width = this.textElement.style.height = "100%";
        this.textElement.id = this.uuid;

        this.textarea = document.createElement('textarea');
        this.mixedFractionElement = document.createElement('div');
        this.mixedFractionElement.classList.add('mixedFraction');
        this.parentLayer.domElement.appendChild(this.mixedFractionElement);
        this._content = options.content ? options.content : "";
        this._inLetterbox = Boolean(options.inLetterbox !== false);
        if (!options.mathJax) {
            this.content = "\\(" + this.content + "\\)";
            Q3.data.layers.layers2D[this.parentLayer.uuid].objects[this.uuid].content = this.content;
            Q3.data.layers.layers2D[this.parentLayer.uuid].objects[this.uuid].mathJax = true;
        }

        this.domElement.appendChild(this.textElement);

        if (Q3.editMode) {
            this.textarea.value = this.content;
            this.domElement.appendChild(this.textarea);
            this.changes = 0;
            this.textarea.addEventListener("input", (e) => {
                this.changes++;
                if (this.changes === 1) {
                    Q3.Events.fire('2d_change_start', [{
                        object: this,
                        properties: ["content"],
                    }]);
                }
                this.setContent(e.target.value);
            })
        }

        const size = RenderManager.getSize();
        this.y = options.y;
        this.x = options.x;
        this.width = options.width || (300 / size.width);
        this.height = options.height || (150 / size.height);
        this.fontSize = options.fontSize || "1.5rem";
        // this.textElement.innerHTML = this.content;
        this.color = 'color' in options ? options.color : '#000000';

        this.sizeSm = options.sizeSm ? options.sizeSm : {
            x: 0.05,
            y: 0.05,
            width: 0.5,
            height: 0.075,
        };

        this.sizeLg = options.sizeLg ? options.sizeLg : {
            x: 0.15,
            y: 0.15,
            width: 0.3375,
            height: 0.3,
        };

        if (Q3.isReady) {
            this.enterExitCtrl = new EntranceExit(this, [this.domElement]);
            this.enterExitCtrl.init(options);
        } else {
            Q3.Events.listen("tourReady", () => {
                this.enterExitCtrl = new EntranceExit(this, [this.domElement]);
                this.enterExitCtrl.init(options);
            })
        }

        if (Q3.editMode) {
            this.domElement.addEventListener('click', e => {
                this.selectHandler(e);
            }, false);
        } else {
            this.domElement.style.pointerEvents = this.textElement.style.pointerEvents = this.disableEvents ? "none" : "auto";
            if (this.hiddenMobile) {
                this.domElement.classList.add("hidden-mobile");
            }
            if (this.hiddenDesktop) {
                this.domElement.classList.add("hidden-desktop");
            }
        }

        const {width, height} = RenderManager.getSize();

        this.redraw = this.redraw.bind(this);
        this.checkSelected = this.checkSelected.bind(this);
        this.initMathJax = this.initMathJax.bind(this);
        this.handleListeners(true);

        this.redraw(width, height);
        this.initMathJax();
        this.addMouseOverListeners(this.domElement)
    }

    checkSelected() {
        if (Q3.selected === this && Q3.editMode && !Q3.multiSelect[this.uuid] && Q3.textEditorActive) {
            this.domElement.classList.add("q3-selected");
        } else if (this.domElement.classList.contains("q3-selected")) {
            this.domElement.classList.remove("q3-selected");
            if (this.changes > 0) {
                Q3.Events.fire('2d_changed');
            }
            this.changes = 0;
            this.initMathJax()
        }
    }

    handleListeners(add) {
        Events[add ? "listen" : "unlisten"]("selectchange", this.checkSelected);
        Events[add ? "listen" : "unlisten"]("viewportchanged", this.redraw);
        Events[add ? "listen" : "unlisten"]("splashremoved", this.redraw);
    }

    playLeavingAnimation() {
        this.enterExitCtrl.playLeave();
    }

    cancelLeavingAnimation() {
        this.enterExitCtrl.cancelLeave();
    }

    setContent(content) {
        this._content = content;
        let layerData = Q3.data.layers.layers2D[this.parentLayer.uuid];
        if (layerData && layerData.objects[this.uuid]) {
            layerData.objects[this.uuid].content = content
        }
    }

    initMathJax() {
        awaitLib().then(() => {
            if (this.content.trim().length) {
                this.textElement.innerHTML = this.content;
                setTimeout(() => {
                    MathJax.typesetPromise([this.textElement])
                }, 0)
            } else {
                this.textElement.innerHTML = "Enter your math";
            }

        })
    }

    oper(o) {
        return `<span class="mq-binary-operator">${o}</span>`
    }

    fraction({numerator, denominator}) {
        return `<span class="mq-fraction mq-non-leaf"><span class="mq-numerator">${numerator}</span><span class="mq-denominator">${denominator}</span></span>`
    }

    float2fraction(x) {
        var tolerance = 1.0E-6;
        var h1 = 1;
        var h2 = 0;
        var k1 = 0;
        var k2 = 1;
        var b = x;
        var z = 0;

        do {
            var a = Math.floor(b);
            var aux = h1;
            h1 = a * h1 + h2;
            h2 = aux;
            aux = k1;
            k1 = a * k1 + k2;
            k2 = aux;
            b = 1 / (b - a);
        } while (Math.abs(x - h1 / k1) > x * tolerance);

        if (h1 > k1) {
            z = Math.floor(h1 / k1);
            h1 -= z * k1;
        }
        return [z, h1, k1];
    }


    lcm(arr) {
        let r1 = 0, r2 = 0;
        let l = arr.length;
        for (let i = 0; i < l; i++) {
            r1 = arr[i] % arr[i + 1];
            if (r1 === 0) {
                arr[i + 1] = (arr[i] * arr[i + 1]) / arr[i + 1];
            } else {
                r2 = arr[i + 1] % r1;
                if (r2 === 0) {
                    arr[i + 1] = (arr[i] * arr[i + 1]) / r1;
                } else {
                    arr[i + 1] = (arr[i] * arr[i + 1]) / r2;
                }
            }
        }
        return arr[l - 1];
    }

    render(width, height) {
        if (!Q3.data.layers.layers2D[this.parentLayer.uuid] || this.disabled || Utils.queryParams.has("disable_2d") || Utils.queryParams.has("disable_text2d")) return;

        if (!this.isMounted || this.target) {
            this.redraw(width, height);
            if (width > 0 && height > 0) {
                this.isMounted = true;
            }
        }
    }

    getRenderPositionAndSize(width, height) {
        const size = RenderManager.getSize();

        if (!width) {
            width = size.width;
        }
        if (!height) {
            height = size.height;
        }

        if (!Q3.editMode && this.inLetterbox !== false) {
            width = Q3.letterbox.offsetWidth / RenderManager.pixelRatio;
            height = (Q3.letterbox.offsetHeight) / RenderManager.pixelRatio;
        }

        let w = this.width * width * RenderManager.pixelRatio;
        let h = this.height * height * RenderManager.pixelRatio;

        if (w < 40) {
            w = 40;
        }
        if (h < 40) {
            h = 40;
        }

        const [x, y] = this._calcPosition(width, height);

        return {x, y, w, h};
    }

    redraw(width, height) {

        !width && (width = RenderManager.domElement.width / RenderManager.pixelRatio);
        !height && (height = (RenderManager.domElement.height) / RenderManager.pixelRatio);

        let currentSize = this.getVWSize();
        if (this[currentSize]) {
            Object.assign(this, this[currentSize]);
        }

        const size = RenderManager.getSize();

        if (!width) {
            width = size.width;
        }

        if (!height) {
            height = size.height;
        }

        const {x, y, w: elWidth, h: elHeight} = this.getRenderPositionAndSize(width, height);

        if (Q3.editMode) {

            if (Q3.mobileView && this.hiddenMobile) {
                this.textElement.style.opacity = 0.4;
            } else if (!Q3.mobileView && this.hiddenDesktop) {
                this.textElement.style.opacity = 0.4;
            } else {
                this.textElement.style.opacity = 1;
            }

        }

        this.domElement.style.width = elWidth + "px";
        this.domElement.style.height = elHeight + "px";
        this.domElement.style.left = (x || 0) + "px";
        this.domElement.style.top = (y || 0) + "px";
        this.domElement.style.fontSize = this.fontSize;
        this.textarea.style.color = this.color;
        this.domElement.style.color = this.color;

    }

    delete() {

        this.handleListeners(false);

        this.parentLayer.objects.splice(this.parentLayer.objects.indexOf(this), 1);
        this.parentLayer.domElement.removeChild(this.domElement);

        delete this.domElement;

        if (Q3.selected === this) {
            Q3.selected = null;
            Events.fire('selectchange');
        }

    }

    clone() {

        // const text = new Text({
        //     content: this.content,
        //     x: this.x + 0.01,
        //     y: this.y + 0.01,
        //     targetUuid: this.targetUuid,
        //     sizeLg: this.sizeLg,
        //     sizeSm: this.sizeSm,
        //     contentConverted: true,
        //   },
        //   this.parentLayer.clone(),
        //   THREE.Math.generateUUID()
        // );
        //
        // Q3.data.layers.layers2D[text.parentLayer.uuid].objects[text.uuid] = {
        //   type: 'text',
        //   content: text.content,
        //   x: text.x,
        //   y: text.y,
        //   targetUuid: text.targetUuid,
        //   sizeLg: text.sizeLg,
        //   sizeSm: text.sizeSm,
        // };
        //
        // this.parentLayer.objects.splice(this.parentLayer.objects.indexOf(this), 0, text);
        //
        // return text;

    }


    setData(data) {
        let currentSize = this.getVWSize();
        Object.assign(this[currentSize], {
            x: data.x,
            y: data.y,
            width: data.width,
            height: data.height
        });

        if (!Q3.data.layers.layers2D[this.parentLayer.uuid].objects[this.uuid][currentSize]) {
            Q3.data.layers.layers2D[this.parentLayer.uuid].objects[this.uuid][currentSize] = {};
        }

        Object.assign(Q3.data.layers.layers2D[this.parentLayer.uuid].objects[this.uuid][currentSize], {
            x: data.x,
            y: data.y,
            width: data.width,
            height: data.height,
        });
        if (currentSize === "sizeSm") {
            this.data.sizeSm.autoResized = true;
        }
        this.redraw();

    }
}

export default MathFormula;
export {MathFormula};
const awaitLib = (function () {
    let promise;
    return function () {
        return promise = (promise || new Promise(resolve => {
            if (window.MathJax) {
                resolve()
            } else {
                console.warn("Begun downloading MathJax");
                injectJS("/js/mathJax/tex-mml-chtml.js").then(function () {
                    console.info("MathJax downloaded.");
                    resolve();
                });
            }

        }));
    };
})();